import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WifiOff, Wifi, ServerCrash, RefreshCw, X } from 'lucide-react';
import { API_ROOT } from '../../config/api';
import styles from './NetworkStatus.module.css';

/**
 * Tells the user, kindly, when the internet is gone or the Hirsta server is not answering,
 * and when everything is back. Place once near the top of the app.
 *
 *   offline  the browser says so, or a quick check finds no internet
 *   server   the internet works but the Hirsta server does not answer (e.g. it is waking up)
 *   back     shown for 3 seconds when things work again
 *
 * It checks again by itself (5 s, 10 s, 20 s, then every 30 s), right away when the tab comes back,
 * and whenever a request of the app fails for a network reason. The screen stays usable underneath.
 */
const PING_URL = `${String(API_ROOT || '').replace(/\/+$/, '')}/`;
const NET_URL = 'https://www.gstatic.com/generate_204';
const DELAYS = [5, 10, 20, 30];
const BACK_MS = 3000;

/** true when the URL answers at all (any HTTP status), false on a network error or timeout */
const reach = async (url, { noCors = false, ms = 6000 } = {}) => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    try {
        await fetch(`${url}${url.includes('?') ? '&' : '?'}_=${Date.now()}`, {
            method: 'GET', cache: 'no-store', signal: ctrl.signal, mode: noCors ? 'no-cors' : 'cors', credentials: 'omit', __networkCheck: true,
        });
        return true;
    } catch (e) {
        return false;
    } finally {
        clearTimeout(t);
    }
};

export default function NetworkStatus() {
    const [status, setStatus] = useState(() => (typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online'));
    const [back, setBack] = useState(false);
    const [checking, setChecking] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [hidden, setHidden] = useState(false);       // the user closed the "server" note
    const attempt = useRef(0);
    const timer = useRef(null);
    const tick = useRef(null);
    const busy = useRef(false);
    const statusRef = useRef(status);

    const setStatusSafe = (next) => {
        const prev = statusRef.current;
        statusRef.current = next;
        setStatus(next);
        if (next === 'online' && prev !== 'online') {
            attempt.current = 0;
            setBack(true);
            setTimeout(() => setBack(false), BACK_MS);
        }
        if (next !== prev) setHidden(false);
    };

    const stopTimers = () => { clearTimeout(timer.current); clearInterval(tick.current); setCountdown(0); };

    const check = useCallback(async () => {
        if (busy.current) return;
        busy.current = true;
        setChecking(true);
        stopTimers();
        try {
            let next;
            if (typeof navigator !== 'undefined' && navigator.onLine === false) next = 'offline';
            else if (await reach(PING_URL)) next = 'online';
            else next = (await reach(NET_URL, { noCors: true, ms: 5000 })) ? 'server' : 'offline';
            setStatusSafe(next);
            if (next !== 'online') {
                // check again by itself, a little later each time
                const wait = DELAYS[Math.min(attempt.current, DELAYS.length - 1)];
                attempt.current += 1;
                setCountdown(wait);
                tick.current = setInterval(() => setCountdown((c) => (c > 1 ? c - 1 : 0)), 1000);
                timer.current = setTimeout(() => { busy.current = false; check(); }, wait * 1000);
            }
        } finally {
            busy.current = false;
            setChecking(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onOffline = () => { stopTimers(); setStatusSafe('offline'); attempt.current = 0; check(); };
        const onOnline = () => { attempt.current = 0; check(); };
        const onVisible = () => { if (document.visibilityState === 'visible' && statusRef.current !== 'online') { attempt.current = 0; check(); } };
        window.addEventListener('offline', onOffline);
        window.addEventListener('online', onOnline);
        document.addEventListener('visibilitychange', onVisible);

        // a request of the app failed for a network reason: look right away (the request itself is not changed)
        const original = window.fetch;
        let lastFail = 0;
        const watched = async (input, init) => {
            try {
                return await original(input, init);
            } catch (e) {
                const ours = init && init.__networkCheck;
                if (!ours && e && e.name !== 'AbortError' && Date.now() - lastFail > 3000) {
                    lastFail = Date.now();
                    setTimeout(() => check(), 0);
                }
                throw e;
            }
        };
        window.fetch = watched;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) check();

        return () => {
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('online', onOnline);
            document.removeEventListener('visibilitychange', onVisible);
            if (window.fetch === watched) window.fetch = original;
            stopTimers();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tryNow = () => { attempt.current = 0; check(); };

    if (status === 'online' && !back) return null;

    if (status === 'online' && back) {
        return (
            <div className={`${styles.wrap}`} role="status" aria-live="polite">
                <div className={`${styles.pill} ${styles.ok}`}>
                    <span className={styles.icon}><Wifi size={16} /></span>
                    <div className={styles.text}><b>You&apos;re back online</b><span>Everything is working again.</span></div>
                </div>
            </div>
        );
    }

    if (status === 'server' && hidden) return null;
    const offline = status === 'offline';
    return (
        <div className={styles.wrap} role="alert" aria-live="assertive">
            <div className={`${styles.pill} ${offline ? styles.off : styles.server}`}>
                <span className={styles.icon}>
                    {offline ? <WifiOff size={16} /> : <ServerCrash size={16} />}
                    <i className={styles.pulse} aria-hidden="true" />
                </span>
                <div className={styles.text}>
                    <b>{offline ? 'You\u2019re offline' : 'Hirsta is taking a moment'}</b>
                    <span>
                        {offline
                            ? 'Check your Wi-Fi or mobile data. Nothing on this screen is lost; we\u2019ll reconnect by ourselves.'
                            : 'Your internet works, but our server is not answering yet. It usually takes a few seconds.'}
                    </span>
                    <small className={styles.meta} aria-live="off">
                        {checking ? 'Checking…' : countdown > 0 ? `Checking again in ${countdown}s` : ''}
                    </small>
                </div>
                <button type="button" className={styles.btn} onClick={tryNow} disabled={checking}>
                    <RefreshCw size={13} className={checking ? styles.spin : ''} /> Try now
                </button>
                {!offline && (
                    <button type="button" className={styles.close} onClick={() => setHidden(true)} aria-label="Hide this message"><X size={14} /></button>
                )}
            </div>
        </div>
    );
}